import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Grid,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { styled, useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogTitle-root": {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: theme.spacing(2),
  },
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
  },
}));

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
    {children}
  </Typography>
);

// Preview estilo WhatsApp
const PreviewBubble = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#075e54" : "#dcf8c6",
  color: theme.palette.mode === "dark" ? "#e9edef" : "#111b21",
  borderRadius: 8,
  padding: theme.spacing(1.5),
  maxWidth: 320,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontSize: "0.875rem",
}));

const LANGUAGES = [
  { value: "pt_BR", label: "Português (Brasil)" },
  { value: "en_US", label: "Inglês (EUA)" },
  { value: "es_ES", label: "Espanhol" },
];

const CATEGORIES = [
  { value: "UTILITY", label: "Utilidade (notificações, atendimento)" },
  { value: "MARKETING", label: "Marketing (promoções, novidades)" },
  { value: "AUTHENTICATION", label: "Autenticação (códigos)" },
];

const HEADER_FORMATS = [
  { value: "NONE", label: "Sem cabeçalho" },
  { value: "TEXT", label: "Texto" },
  { value: "IMAGE", label: "Imagem" },
  { value: "VIDEO", label: "Vídeo" },
  { value: "DOCUMENT", label: "Documento" },
];

const BUTTON_TYPES = [
  { value: "QUICK_REPLY", label: "Resposta rápida" },
  { value: "URL", label: "Link (URL)" },
  { value: "PHONE_NUMBER", label: "Telefone" },
];

const emptyState = () => ({
  name: "",
  language: "pt_BR",
  category: "UTILITY",
  headerFormat: "NONE",
  headerText: "",
  headerHandle: "",
  headerFileName: "",
  bodyText: "",
  footerText: "",
  buttons: [],
  carouselEnabled: false,
  cards: [],
  // Autenticação (OTP)
  otpType: "COPY_CODE",
  otpExpiration: "",
  otpButtonText: "",
  otpSecurity: false,
});

const MIME_BY_FORMAT = {
  IMAGE: "image/*",
  VIDEO: "video/*",
  DOCUMENT: ".pdf",
};

// Conta as variáveis {{n}} num texto
const countVars = (text) => {
  const matches = (text || "").match(/\{\{\s*\d+\s*\}\}/g) || [];
  const nums = matches.map((m) => parseInt(m.replace(/\D/g, ""), 10));
  return nums.length ? Math.max(...nums) : 0;
};

const MetaTemplateModal = ({ open, onClose, templateId }) => {
  const theme = useTheme();
  const [state, setState] = useState(emptyState());
  const [bodyExamples, setBodyExamples] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isEdit = Boolean(templateId);

  const set = (patch) => setState((s) => ({ ...s, ...patch }));

  // Faz upload do arquivo para a Meta (via backend) e devolve o handle
  const uploadFile = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/metaTemplates/upload-media", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data; // { handle, fileName, mimeType }
  };

  const handleHeaderFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { handle, fileName } = await uploadFile(file);
      set({ headerHandle: handle, headerFileName: fileName });
      toast.success("Mídia enviada.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleCardFile = async (idx, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { handle, fileName } = await uploadFile(file);
      updateCard(idx, { imageHandle: handle, imageFileName: fileName });
      toast.success("Imagem do card enviada.");
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (!templateId) {
      setState(emptyState());
      setBodyExamples([]);
      return;
    }
    const fetch = async () => {
      try {
        const { data } = await api.get(`/metaTemplates/${templateId}`);
        const comps = data.components || [];
        const header = comps.find((c) => c.type === "HEADER");
        const body = comps.find((c) => c.type === "BODY");
        const footer = comps.find((c) => c.type === "FOOTER");
        const buttonsComp = comps.find((c) => c.type === "BUTTONS");
        const carousel = comps.find((c) => c.type === "CAROUSEL");

        set({
          name: data.name,
          language: data.language,
          category: data.category,
          headerFormat: header ? header.format || "TEXT" : "NONE",
          headerText: header?.text || "",
          headerHandle: header?.example?.header_handle?.[0] || "",
          headerFileName: header?.example?.header_handle?.[0] ? "mídia enviada" : "",
          bodyText: body?.text || "",
          footerText: footer?.text || "",
          buttons: (buttonsComp?.buttons || []).map((b) => ({
            type: b.type,
            text: b.text || "",
            url: b.url || "",
            phone_number: b.phone_number || "",
          })),
          carouselEnabled: Boolean(carousel),
          cards: (carousel?.cards || []).map((card) => {
            const ch = card.components?.find((c) => c.type === "HEADER");
            const cb = card.components?.find((c) => c.type === "BODY");
            const cbtn = card.components?.find((c) => c.type === "BUTTONS");
            return {
              imageHandle: ch?.example?.header_handle?.[0] || "",
              imageFileName: ch?.example?.header_handle?.[0] ? "imagem enviada" : "",
              bodyText: cb?.text || "",
              buttons: (cbtn?.buttons || []).map((b) => ({
                type: b.type,
                text: b.text || "",
                url: b.url || "",
              })),
            };
          }),
        });
        setBodyExamples(body?.example?.body_text?.[0] || []);
      } catch (err) {
        toastError(err);
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, open]);

  // Ajusta a quantidade de exemplos do body conforme as variáveis
  const bodyVarCount = countVars(state.bodyText);
  useEffect(() => {
    setBodyExamples((prev) => {
      const next = [...prev];
      next.length = bodyVarCount;
      for (let i = 0; i < bodyVarCount; i += 1) {
        if (!next[i]) next[i] = "";
      }
      return next;
    });
  }, [bodyVarCount]);

  const addButton = () =>
    set({
      buttons: [...state.buttons, { type: "QUICK_REPLY", text: "", url: "", phone_number: "" }],
    });
  const updateButton = (idx, patch) => {
    const buttons = state.buttons.map((b, i) => (i === idx ? { ...b, ...patch } : b));
    set({ buttons });
  };
  const removeButton = (idx) =>
    set({ buttons: state.buttons.filter((_, i) => i !== idx) });

  const addCard = () =>
    set({
      cards: [
        ...state.cards,
        { imageHandle: "", imageFileName: "", bodyText: "", buttons: [] },
      ],
    });
  const updateCard = (idx, patch) =>
    set({ cards: state.cards.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  const removeCard = (idx) =>
    set({ cards: state.cards.filter((_, i) => i !== idx) });

  // Monta o array de componentes no formato Meta
  const buildComponents = () => {
    // AUTENTICAÇÃO (OTP): estrutura especial. A Meta gera o corpo automaticamente;
    // configuramos só o botão OTP (copiar código / one-tap), validade e recomendação.
    if (state.category === "AUTHENTICATION") {
      const comps = [{ type: "BODY", add_security_recommendation: !!state.otpSecurity }];
      if (state.otpExpiration) {
        comps.push({ type: "FOOTER", code_expiration_minutes: Number(state.otpExpiration) });
      }
      const otpBtn = { type: "OTP", otp_type: state.otpType || "COPY_CODE" };
      // COPY_CODE usa o texto padrão da Meta (não aceita text custom);
      // one/zero-tap usam autofill_text.
      if (state.otpType !== "COPY_CODE" && state.otpButtonText.trim()) {
        otpBtn.autofill_text = state.otpButtonText.trim();
      }
      comps.push({ type: "BUTTONS", buttons: [otpBtn] });
      return comps;
    }

    const components = [];

    if (state.headerFormat === "TEXT" && state.headerText.trim()) {
      components.push({ type: "HEADER", format: "TEXT", text: state.headerText.trim() });
    } else if (
      ["IMAGE", "VIDEO", "DOCUMENT"].includes(state.headerFormat) &&
      state.headerHandle
    ) {
      components.push({
        type: "HEADER",
        format: state.headerFormat,
        example: { header_handle: [state.headerHandle] },
      });
    }

    const body = { type: "BODY", text: state.bodyText.trim() };
    if (bodyVarCount > 0) {
      body.example = { body_text: [bodyExamples.map((e) => e || "exemplo")] };
    }
    components.push(body);

    if (state.footerText.trim()) {
      components.push({ type: "FOOTER", text: state.footerText.trim() });
    }

    if (state.buttons.length) {
      components.push({
        type: "BUTTONS",
        buttons: state.buttons.map((b) => {
          if (b.type === "URL") return { type: "URL", text: b.text, url: b.url };
          if (b.type === "PHONE_NUMBER")
            return { type: "PHONE_NUMBER", text: b.text, phone_number: b.phone_number };
          return { type: "QUICK_REPLY", text: b.text };
        }),
      });
    }

    if (state.carouselEnabled && state.cards.length) {
      components.push({
        type: "CAROUSEL",
        cards: state.cards.map((card) => {
          const cardComps = [];
          if (card.imageHandle) {
            cardComps.push({
              type: "HEADER",
              format: "IMAGE",
              example: { header_handle: [card.imageHandle] },
            });
          }
          cardComps.push({ type: "BODY", text: card.bodyText.trim() });
          if (card.buttons.length) {
            cardComps.push({
              type: "BUTTONS",
              buttons: card.buttons.map((b) =>
                b.type === "URL"
                  ? { type: "URL", text: b.text, url: b.url }
                  : { type: "QUICK_REPLY", text: b.text }
              ),
            });
          }
          return { components: cardComps };
        }),
      });
    }

    return components;
  };

  const handleSave = async () => {
    if (!state.name.trim()) {
      toast.error("Informe o nome do template.");
      return;
    }
    if (!/^[a-z0-9_]+$/.test(state.name)) {
      toast.error(
        "Nome inválido: use apenas letras minúsculas, números e underline (ex: confirmacao_pedido)."
      );
      return;
    }
    // Autenticação (OTP) não exige corpo — a Meta gera o texto do código.
    if (state.category !== "AUTHENTICATION" && !state.bodyText.trim()) {
      toast.error("O corpo (body) é obrigatório.");
      return;
    }

    // Limites da Meta para botões: até 2 URL, 1 telefone e 10 botões no total.
    if (state.buttons.length) {
      const urlCount = state.buttons.filter((b) => b.type === "URL").length;
      const phoneCount = state.buttons.filter((b) => b.type === "PHONE_NUMBER").length;
      const emptyText = state.buttons.some((b) => !b.text || !b.text.trim());
      if (emptyText) {
        toast.error("Todo botão precisa de um texto.");
        return;
      }
      if (urlCount > 2) {
        toast.error("A Meta permite no máximo 2 botões de URL.");
        return;
      }
      if (phoneCount > 1) {
        toast.error("A Meta permite no máximo 1 botão de telefone.");
        return;
      }
      if (state.buttons.length > 10) {
        toast.error("A Meta permite no máximo 10 botões.");
        return;
      }
    }

    const components = buildComponents();
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/metaTemplates/${templateId}`, {
          category: state.category,
          components,
        });
        toast.success("Template atualizado e reenviado para aprovação.");
      } else {
        await api.post("/metaTemplates", {
          name: state.name.trim(),
          language: state.language,
          category: state.category,
          components,
        });
        toast.success("Template enviado para aprovação da Meta.");
      }
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // Texto do preview com variáveis substituídas pelos exemplos
  const previewBody = (() => {
    let txt = state.bodyText;
    bodyExamples.forEach((ex, i) => {
      txt = txt.replace(new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, "g"), ex || `{{${i + 1}}}`);
    });
    return txt;
  })();

  const isMedia = ["IMAGE", "VIDEO", "DOCUMENT"].includes(state.headerFormat);

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle>
        {isEdit ? "Editar template oficial" : "Novo template oficial (WABA)"}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Coluna do builder */}
          <Grid item xs={12} md={7}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Nome do template"
                  fullWidth
                  size="small"
                  value={state.name}
                  disabled={isEdit}
                  helperText="minúsculas, números e _"
                  onChange={(e) =>
                    set({ name: e.target.value.toLowerCase().replace(/\s/g, "_") })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Idioma"
                  fullWidth
                  size="small"
                  value={state.language}
                  disabled={isEdit}
                  onChange={(e) => set({ language: e.target.value })}
                >
                  {LANGUAGES.map((l) => (
                    <MenuItem key={l.value} value={l.value}>
                      {l.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Categoria"
                  fullWidth
                  size="small"
                  value={state.category}
                  onChange={(e) => set({ category: e.target.value })}
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {state.category === "AUTHENTICATION" && (
              <>
                <SectionTitle>Autenticação (código OTP)</SectionTitle>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Para autenticação, a Meta gera o texto do código automaticamente.
                  Preencha só os campos abaixo — cabeçalho, corpo e botões comuns são ignorados.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Tipo de OTP"
                      fullWidth
                      size="small"
                      value={state.otpType}
                      onChange={(e) => set({ otpType: e.target.value })}
                    >
                      <MenuItem value="COPY_CODE">Copiar código</MenuItem>
                      <MenuItem value="ONE_TAP">Toque único (autofill)</MenuItem>
                      <MenuItem value="ZERO_TAP">Zero toque</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Validade do código (min)"
                      type="number"
                      fullWidth
                      size="small"
                      value={state.otpExpiration}
                      onChange={(e) => set({ otpExpiration: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Texto de autofill (só one/zero-tap)"
                      fullWidth
                      size="small"
                      value={state.otpButtonText}
                      onChange={(e) => set({ otpButtonText: e.target.value })}
                      disabled={state.otpType === "COPY_CODE"}
                      helperText={state.otpType === "COPY_CODE" ? "Copiar código usa o texto padrão da Meta" : ""}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Recomendação de segurança"
                      fullWidth
                      size="small"
                      value={state.otpSecurity ? "sim" : "nao"}
                      onChange={(e) => set({ otpSecurity: e.target.value === "sim" })}
                    >
                      <MenuItem value="nao">Não</MenuItem>
                      <MenuItem value="sim">Sim</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Cabeçalho */}
            <SectionTitle>Cabeçalho (opcional)</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  label="Tipo"
                  fullWidth
                  size="small"
                  value={state.headerFormat}
                  onChange={(e) => set({ headerFormat: e.target.value })}
                >
                  {HEADER_FORMATS.map((h) => (
                    <MenuItem key={h.value} value={h.value}>
                      {h.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {state.headerFormat === "TEXT" && (
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Texto do cabeçalho"
                    fullWidth
                    size="small"
                    value={state.headerText}
                    onChange={(e) => set({ headerText: e.target.value })}
                  />
                </Grid>
              )}
              {isMedia && (
                <Grid item xs={12} sm={8}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      startIcon={<UploadFileIcon />}
                      disabled={uploading}
                    >
                      {uploading ? "Enviando…" : "Enviar arquivo de exemplo"}
                      <input
                        type="file"
                        hidden
                        accept={MIME_BY_FORMAT[state.headerFormat] || "*/*"}
                        onChange={(e) => handleHeaderFile(e.target.files?.[0])}
                      />
                    </Button>
                    {state.headerFileName && (
                      <Typography variant="caption" color="success.main">
                        ✓ {state.headerFileName}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    A Meta usa este arquivo de exemplo para aprovar o modelo.
                  </Typography>
                </Grid>
              )}
            </Grid>

            {/* Corpo */}
            <SectionTitle>Corpo da mensagem *</SectionTitle>
            <TextField
              multiline
              minRows={4}
              fullWidth
              size="small"
              placeholder="Olá {{1}}, seu atendimento {{2}} foi registrado."
              value={state.bodyText}
              onChange={(e) => set({ bodyText: e.target.value })}
            />
            <Typography variant="caption" color="textSecondary">
              Use {"{{1}}"}, {"{{2}}"}… para variáveis. Detectadas: {bodyVarCount}
            </Typography>
            {bodyVarCount > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Exemplos das variáveis (para aprovação da Meta):
                </Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  {bodyExamples.map((ex, i) => (
                    <Grid item xs={6} sm={4} key={i}>
                      <TextField
                        label={`Exemplo {{${i + 1}}}`}
                        fullWidth
                        size="small"
                        value={ex}
                        onChange={(e) => {
                          const next = [...bodyExamples];
                          next[i] = e.target.value;
                          setBodyExamples(next);
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Rodapé */}
            <SectionTitle>Rodapé (opcional)</SectionTitle>
            <TextField
              fullWidth
              size="small"
              placeholder="Ex: Sabe Sistemas"
              value={state.footerText}
              onChange={(e) => set({ footerText: e.target.value })}
            />

            {/* Botões */}
            <SectionTitle>
              Botões (opcional){" "}
              <Button size="small" startIcon={<AddIcon />} onClick={addButton}>
                Adicionar
              </Button>
            </SectionTitle>
            {state.buttons.map((b, idx) => (
              <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    label="Tipo"
                    fullWidth
                    size="small"
                    value={b.type}
                    onChange={(e) => updateButton(idx, { type: e.target.value })}
                  >
                    {BUTTON_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Texto do botão"
                    fullWidth
                    size="small"
                    value={b.text}
                    onChange={(e) => updateButton(idx, { text: e.target.value })}
                  />
                </Grid>
                {b.type === "URL" && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="URL"
                      fullWidth
                      size="small"
                      value={b.url}
                      onChange={(e) => updateButton(idx, { url: e.target.value })}
                    />
                  </Grid>
                )}
                {b.type === "PHONE_NUMBER" && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Telefone (+55...)"
                      fullWidth
                      size="small"
                      value={b.phone_number}
                      onChange={(e) =>
                        updateButton(idx, { phone_number: e.target.value })
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={1}>
                  <IconButton size="small" onClick={() => removeButton(idx)}>
                    <DeleteIcon color="error" fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            {/* Carrossel */}
            <SectionTitle>
              Carrossel (até 10 cards){" "}
              <Button
                size="small"
                onClick={() =>
                  set({ carouselEnabled: !state.carouselEnabled })
                }
              >
                {state.carouselEnabled ? "Desativar" : "Ativar"}
              </Button>
            </SectionTitle>
            {state.carouselEnabled && (
              <Box>
                {state.cards.map((card, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{ p: 1.5, mb: 1 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Card {idx + 1}
                      </Typography>
                      <IconButton size="small" onClick={() => removeCard(idx)}>
                        <DeleteIcon color="error" fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        startIcon={<UploadFileIcon />}
                        disabled={uploading}
                      >
                        {uploading ? "Enviando…" : "Imagem do card"}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => handleCardFile(idx, e.target.files?.[0])}
                        />
                      </Button>
                      {card.imageFileName && (
                        <Typography variant="caption" color="success.main">
                          ✓ {card.imageFileName}
                        </Typography>
                      )}
                    </Box>
                    <TextField
                      label="Texto do card"
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      sx={{ mt: 1 }}
                      value={card.bodyText}
                      onChange={(e) => updateCard(idx, { bodyText: e.target.value })}
                    />
                  </Paper>
                ))}
                {state.cards.length < 10 && (
                  <Button size="small" startIcon={<AddIcon />} onClick={addCard}>
                    Adicionar card
                  </Button>
                )}
              </Box>
            )}
          </Grid>

          {/* Coluna do preview */}
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Pré-visualização
            </Typography>
            <Box
              sx={{
                backgroundColor: theme.palette.mode === "dark" ? "#0b141a" : "#e5ddd5",
                borderRadius: 2,
                p: 2,
                minHeight: 200,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <PreviewBubble elevation={1}>
                {state.headerFormat === "TEXT" && state.headerText && (
                  <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                    {state.headerText}
                  </Typography>
                )}
                {isMedia && (
                  <Box
                    sx={{
                      backgroundColor: "rgba(0,0,0,0.15)",
                      borderRadius: 1,
                      height: 100,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 0.5,
                      fontSize: 12,
                    }}
                  >
                    [{state.headerFormat}]
                  </Box>
                )}
                <Typography variant="body2">
                  {previewBody || "Corpo da mensagem aparece aqui…"}
                </Typography>
                {state.footerText && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.5, opacity: 0.7 }}
                  >
                    {state.footerText}
                  </Typography>
                )}
                {state.buttons.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Divider sx={{ mb: 0.5 }} />
                    {state.buttons.map((b, i) => (
                      <Typography
                        key={i}
                        variant="body2"
                        sx={{ textAlign: "center", color: "#0277bd", py: 0.3 }}
                      >
                        {b.text || "Botão"}
                      </Typography>
                    ))}
                  </Box>
                )}
                {state.carouselEnabled && state.cards.length > 0 && (
                  <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                    🎠 Carrossel com {state.cards.length} card(s)
                  </Typography>
                )}
              </PreviewBubble>
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: "block", mt: 2 }}
            >
              Após salvar, o template é enviado para aprovação da Meta. O status
              (Em análise → Aprovado/Reprovado) aparece na lista; use o botão
              Sincronizar para atualizar.
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={saving}>
          Cancelar
        </Button>
        <ButtonWithSpinner
          loading={saving}
          disabled={uploading}
          onClick={handleSave}
          color="primary"
          variant="contained"
        >
          {isEdit ? "Salvar alterações" : "Enviar para aprovação"}
        </ButtonWithSpinner>
      </DialogActions>
    </StyledDialog>
  );
};

export default MetaTemplateModal;
